import { useState, useEffect, useCallback, useRef } from 'react';
import { DateTime } from 'luxon';
import apiService from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logging/logger';
import { instructorClassWindow } from './instructorHome/instructorHome';

// Instructor-facing mirror of useActiveAttendanceSessions.js — same polling
// cadence and today/visible-window logic, scoped to sessions this instructor
// is running rather than sessions a student is enrolled in.
export default function useInstructorActiveAttendanceSessions() {
  const { user } = useAuth();
  const userId = user?.id;
  const userRole = user?.role;
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [now, setNow] = useState(() => DateTime.now().setZone('America/Chicago'));
  const isFetchingRef = useRef(false);

  // Class start/end transitions should not wait for the two-minute API poll.
  useEffect(() => {
    if (!sessions.length) return;
    const tick = () => setNow(DateTime.now().setZone('America/Chicago'));
    tick();
    const timer = setInterval(tick, 1000);
    window.addEventListener('focus', tick);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', tick);
    };
  }, [sessions.length]);

  const fetchActiveSessions = useCallback(async () => {
    if (!userId || userRole !== 'instructor') {
      setSessions([]);
      setInitialLoadDone(true);
      return;
    }
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    try {
      setLoading(true);
      const response = await apiService.request('/attendance/instructor/active-today?v=' + Date.now());
      if (response.success) {
        setSessions(response.sessions || []);
      }
    } catch (error) {
      if (error.status !== 403) {
        logger.error('Failed to fetch instructor active sessions', { error });
      }
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
      isFetchingRef.current = false;
    }
  }, [userId, userRole]);

  useEffect(() => {
    if (!userId || userRole !== 'instructor') {
      setSessions([]);
      return undefined;
    }

    fetchActiveSessions();

    const handleFocus = () => fetchActiveSessions();
    const handleVisibilityChange = () => { if (!document.hidden) fetchActiveSessions(); };
    const interval = setInterval(fetchActiveSessions, 120000);

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, userRole, fetchActiveSessions]);

  const getVisibleSessions = () => {
    return sessions.filter((session) => {
      if (!session.end_time) return false;
      const endTime = DateTime.fromISO(
        session.end_time instanceof Date ? session.end_time.toISOString() : session.end_time,
        { zone: 'utc' }
      ).setZone('America/Chicago');
      return endTime.isValid && now <= endTime;
    }).map(session => ({ ...session, isActive: instructorClassWindow(session, now).live }));
  };

  return { sessions, loading, initialLoadDone, getVisibleSessions };
}
